import { motion } from 'motion/react';

import { AIAgentBanner } from '@src/components/AIAgentBanner';
import { ScreenshotGallery } from '@src/components/ScreenshotGallery';
import { Button } from '@src/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@src/components/ui/card';
import { useNavigateToTop } from '@src/hooks/useNavigateToTop';

// Screenshot
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BookOpen,
  Building2,
  CheckCircle,
  CircleAlert,
  Clock,
  Database,
  FileCheck,
  Gauge,
  Headphones,
  Heart,
  Layers,
  Mail,
  MessageSquare,
  RotateCcw,
  Shield,
  ShoppingCart,
  Smartphone,
  Target,
  Ticket,
  Timer,
  Users,
  Workflow,
  XCircle,
} from 'lucide-react';

import itsmDashboard from '@src/assets/screenshots/itsm-dashboard.webp';
import itsmKnowledgeBase from '@src/assets/screenshots/itsm-knowledge-base.webp';
import itsmServiceCatalog from '@src/assets/screenshots/itsm-service-catalog.webp';
import itsmSlaPolicies from '@src/assets/screenshots/itsm-sla-policies.webp';
import itsmTickets from '@src/assets/screenshots/itsm-tickets.webp';
import AnswerSection from '@src/components/seo/AnswerSection';

const BOSSS_BLUE = '#2973a3';
const AMBER = '#f59e0b';
const BG_COLOR = '#F8FAFC';

// Pain points for ITSM
const painPoints = [
  {
    icon: XCircle,
    problem: 'Disconnected Ticketing Systems',
    description: 'Support requests scattered across email, chat, and legacy tools with no unified view or tracking.',
    solution: 'Unified service desk with omnichannel ticket intake and centralized management.',
  },
  {
    icon: Timer,
    problem: 'SLA Breaches & Blind Spots',
    description: 'No visibility into response times, resolution rates, or pending SLA violations until it\'s too late.',
    solution: 'Real-time SLA monitoring with proactive breach alerts and performance dashboards.',
  },
  {
    icon: AlertTriangle,
    problem: 'Knowledge Silos',
    description: 'Tribal knowledge locked in individual heads, leading to repeated escalations and slow resolutions.',
    solution: 'Searchable knowledge base with solution articles, procedures, and self-service portal.',
  },
  {
    icon: Layers,
    problem: 'No Asset Context',
    description: 'Technicians troubleshoot without knowing the user\'s devices, software, or recent changes.',
    solution: 'Direct CMDB integration links tickets to affected assets with full configuration history.',
  },
];

// ITSM Capabilities
const capabilities = [
  {
    icon: Ticket,
    title: 'Ticket Management',
    description: 'Track incidents, requests, and problems with customizable workflows and SLA tracking.',
    features: ['List & Kanban views', 'Priority management', 'Assignee routing', 'Custom fields'],
  },
  {
    icon: BookOpen,
    title: 'Knowledge Base',
    description: 'Build a searchable repository of solutions, procedures, and FAQs for faster resolution.',
    features: ['Article management', 'Search & discovery', 'Self-service portal', 'Version history'],
  },
  {
    icon: ShoppingCart,
    title: 'Service Catalog',
    description: 'Define and publish IT services with approval workflows and fulfillment tracking.',
    features: ['Service definitions', 'Request forms', 'Approval chains', 'SLA assignment'],
  },
  {
    icon: Target,
    title: 'SLA Management',
    description: 'Define service level agreements and monitor compliance with real-time dashboards.',
    features: ['SLA policies', 'Breach alerts', 'Performance reports', 'Escalation rules'],
  },
];

// Ticket types
const ticketTypes = [
  { icon: AlertTriangle, name: 'Incident', desc: 'Report an issue or service disruption', color: '#ef4444' },
  { icon: Headphones, name: 'Service Request', desc: 'Request a new service or access', color: '#3b82f6' },
  { icon: CircleAlert, name: 'Problem', desc: 'Identify root cause of recurring incidents', color: '#f97316' },
  { icon: RotateCcw, name: 'Change Request', desc: 'Request a change to infrastructure', color: '#10b981' },
  { icon: FileCheck, name: 'CAB Review', desc: 'Change Advisory Board approval', color: '#8b5cf6' },
];

// Dashboard metrics
const dashboardMetrics = [
  { label: 'Open Tickets', value: '4', change: 'Across all queues', color: AMBER },
  { label: 'High Priority', value: '3', change: 'Needs triage', color: '#ef4444' },
  { label: 'Pending Approvals', value: '2', change: 'Awaiting CAB', color: BOSSS_BLUE },
  { label: 'Assignments / Day', value: '0.3', change: 'Per analyst', color: '#10b981' },
];

// Integrations
const integrations = [
  { name: 'CMDB', icon: Database, description: 'Asset context on every ticket' },
  { name: 'IAM', icon: Users, description: 'User identity & access info' },
  { name: 'XDR', icon: Shield, description: 'Security alert integration' },
  { name: 'Email', icon: Mail, description: 'Email-to-ticket conversion' },
  { name: 'Slack', icon: MessageSquare, description: 'Chat-based ticket creation' },
  { name: 'Mobile', icon: Smartphone, description: 'Mobile app for technicians' },
];

export function ServiceDeskPage() {
  const onNavigateToTop = useNavigateToTop();

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 lg:py-28" style={{ background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 50%, #fde68a 100%)' }}>
        <div className="absolute inset-0 pointer-events-none">
          <motion.div
            className="absolute top-20 left-20"
            animate={{ rotate: [0, 10, -10, 0], y: [0, -15, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Headphones className="w-8 h-8" style={{ color: `${AMBER}50` }} />
          </motion.div>

          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            className="absolute bottom-40 right-20"
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Ticket className="w-6 h-6" style={{ color: `${AMBER}40` }} />
          </motion.div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              initial={{ opacity: 0, x: -50 }}
            >
              <div
                style={{ backgroundColor: `${AMBER}15`, color: AMBER }}
                className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-6"
              >
                <Headphones className="w-4 h-4 mr-2" />
                IT Service Management
              </div>

              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                IT Support That Actually Works
              </h1>

              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                BOSSS Service Desk unifies incident management, service requests, and change
                control in a single platform—connected directly to your assets and security operations.
              </p>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                {dashboardMetrics.map((metric, index) => (
                  <motion.div
                    key={index}
                    animate={{ opacity: 1, y: 0 }}
                    initial={{ opacity: 0, y: 20 }}
                    transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                    className="text-center p-3 bg-white/80 rounded-lg shadow-sm"
                  >
                    <div className="text-2xl font-bold" style={{ color: metric.color }}>{metric.value}</div>
                    <div className="text-xs text-gray-600">{metric.label}</div>
                  </motion.div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <motion.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }}>
                  <Button
                    style={{ backgroundColor: AMBER }}
                    className="text-white px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-xl"
                    onClick={() => onNavigateToTop('/demo')}
                  >
                    Schedule a Demo
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </motion.div>

                <motion.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }}>
                  <Button
                    variant="outline"
                    style={{ borderColor: AMBER, color: AMBER }}
                    className="px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-lg"
                    onClick={() => onNavigateToTop('/contact')}
                  >
                    Contact Sales
                  </Button>
                </motion.div>
              </div>
            </motion.div>

            {/* Dashboard Preview */}
            <motion.div
              className="relative"
              animate={{ opacity: 1, x: 0 }}
              initial={{ opacity: 0, x: 50 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="bg-white rounded-xl shadow-2xl p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div style={{ backgroundColor: `${AMBER}15` }} className="w-10 h-10 rounded-lg flex items-center justify-center">
                      <Gauge className="w-5 h-5" style={{ color: AMBER }} />
                    </div>

                    <div>
                      <h3 className="font-semibold text-gray-900">Service Desk Dashboard</h3>
                      <p className="text-xs text-gray-500">Real-time operations overview</p>
                    </div>
                  </div>

                  <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">Live</span>
                </div>

                {/* Ticket Types Preview */}
                <div className="mb-6">
                  <p className="text-sm font-medium text-gray-700 mb-3">Ticket Types</p>

                  <div className="grid grid-cols-2 gap-2">
                    {ticketTypes.slice(0, 4).map((type, i) => (
                      <motion.div
                        key={i}
                        animate={{ opacity: 1, scale: 1 }}
                        initial={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.3, delay: 0.5 + i * 0.05 }}
                        className="bg-gray-50 rounded-lg p-3 flex items-center gap-2"
                      >
                        <type.icon className="w-4 h-4" style={{ color: type.color }} />
                        <span className="text-xs font-medium text-gray-700">{type.name}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* SLA Status */}
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { icon: Clock, label: 'Pending', value: '12', color: AMBER },
                    { icon: Activity, label: 'In Progress', value: '8', color: BOSSS_BLUE },
                    { icon: CheckCircle, label: 'Resolved', value: '156', color: '#10b981' },
                  ].map((stat, i) => (
                    <div key={i} className="bg-gray-50 rounded-lg p-3 text-center">
                      <stat.icon style={{ color: stat.color }} className="w-4 h-4 mx-auto mb-1" />
                      <p className="text-lg font-bold text-gray-900">{stat.value}</p>
                      <p className="text-xs text-gray-500">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Answer-first overview (SEO-82) */}
      <AnswerSection path="/platform/service-desk" />


      {/* Pain Points Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-16"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              End the IT Support Chaos
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Traditional service desks create friction. Here&apos;s how BOSSS Service Desk
              streamlines IT operations and improves user satisfaction.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {painPoints.map((point, index) => (
              <motion.div
                key={index}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="h-full border-gray-200 hover:shadow-xl transition-all duration-300 overflow-hidden">
                  <CardHeader className="pb-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-red-100">
                        <point.icon className="w-6 h-6 text-red-600" />
                      </div>

                      <div>
                        <CardTitle className="text-xl text-gray-900 mb-2">
                          {point.problem}
                        </CardTitle>

                        <p className="text-sm text-gray-500">{point.description}</p>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent>
                    <div style={{ backgroundColor: `${AMBER}08` }} className="flex items-start gap-3 p-4 rounded-lg">
                      <CheckCircle style={{ color: AMBER }} className="w-5 h-5 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-gray-700">{point.solution}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Screenshot Section */}
      <section className="py-20 bg-gradient-to-b from-white to-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              See It In Action
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Unified service desk with incident management, change requests, and service catalog—all in one platform.
            </p>
          </motion.div>

          {/* Screenshot Tabs */}
          <ScreenshotGallery
            urlPath="itsm"
            accentColor={AMBER}
            screenshots={[
              { src: itsmDashboard, label: 'Dashboard', alt: 'BOSSS Service Desk dashboard with ticket volume, SLA attainment and change throughput' },
              { src: itsmTickets, label: 'Tickets', alt: 'BOSSS Service Desk ticket queue covering incidents, requests and changes' },
              { src: itsmServiceCatalog, label: 'Service Catalog', alt: 'BOSSS Service Desk service catalog of requestable services' },
              { src: itsmKnowledgeBase, label: 'Knowledge Base', alt: 'BOSSS Service Desk knowledge base articles supporting self-service resolution' },
              { src: itsmSlaPolicies, label: 'SLA Policies', alt: 'BOSSS Service Desk SLA policies defining response and resolution targets' },
            ]}
          />

          {/* Feature Callouts */}
          <motion.div
            viewport={{ once: true }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12"
          >
            {[
              { icon: Ticket, label: 'Ticket Types', value: '5 types' },
              { icon: Clock, label: 'SLA Tracking', value: 'Automated' },
              { icon: Workflow, label: 'Workflows', value: 'Customizable' },
              { icon: ShoppingCart, label: 'Service Catalog', value: 'Self-service' },
            ].map((item, i) => (
              <div key={i} className="text-center p-4 bg-white rounded-xl shadow-md border border-gray-100">
                <item.icon style={{ color: AMBER }} className="w-6 h-6 mx-auto mb-2" />
                <p className="text-lg font-bold text-gray-900">{item.value}</p>
                <p className="text-xs text-gray-500">{item.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-20" style={{ backgroundColor: BG_COLOR }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Complete ITSM Capabilities
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to manage IT services efficiently, from ticket creation to resolution.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {capabilities.map((capability, index) => (
              <motion.div
                key={index}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="h-full border-gray-200 hover:shadow-xl transition-all duration-300 group">
                  <CardHeader className="pb-2">
                    <div
                      style={{ backgroundColor: `${AMBER}15` }}
                      className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                    >
                      <capability.icon className="w-6 h-6" style={{ color: AMBER }} />
                    </div>

                    <CardTitle className="text-lg group-hover:text-amber-600 transition-colors">
                      {capability.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent>
                    <p className="text-sm text-gray-600 mb-4">{capability.description}</p>

                    <ul className="space-y-1">
                      {capability.features.map((feature, i) => (
                        <li key={i} className="flex items-center text-xs text-gray-500">
                          <CheckCircle className="w-3 h-3 mr-2" style={{ color: AMBER }} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Ticket Types Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              5 Ticket Types for Every Scenario
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              ITIL-aligned ticket types with customizable workflows for incidents, requests, problems, and changes.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {ticketTypes.map((type, i) => (
              <motion.div
                key={i}
                viewport={{ once: true }}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: i * 0.05 }}
              >
                <Card className="h-full border-gray-200 hover:shadow-lg transition-all group text-center p-6">
                  <div
                    style={{ backgroundColor: `${type.color}15` }}
                    className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform"
                  >
                    <type.icon className="w-7 h-7" style={{ color: type.color }} />
                  </div>

                  <h4 className="font-semibold text-gray-900 mb-2">{type.name}</h4>
                  <p className="text-sm text-gray-500">{type.desc}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Integrations Section */}
      <section className="py-20" style={{ backgroundColor: BG_COLOR }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Connected to Your Entire Stack
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Service Desk integrates natively with BOSSS modules and external tools for seamless operations.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {integrations.map((integration, i) => (
              <motion.div
                key={i}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
              >
                <Card className="h-full text-center p-4 hover:shadow-md transition-all group">
                  <integration.icon style={{ color: BOSSS_BLUE }} className="w-8 h-8 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                  <p className="text-sm font-semibold text-gray-900">{integration.name}</p>
                  <p className="text-xs text-gray-500">{integration.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Agent Banner */}
      <AIAgentBanner
        toolsCount={15}
        accentColor="#f59e0b"
        agentName="Support Agent"
        description="Your AI-powered service desk copilot. Ask about tickets, SLA status, or knowledge base articles—get instant answers."
        exampleQueries={[
          '"Show me open tickets assigned to me"',
          '"Create a new service request"',
          '"What tickets are approaching SLA breach?"',
          '"Find knowledge articles about VPN issues"',
        ]}
      />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-amber-500 to-orange-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Ready to Transform IT Support?
            </h2>

            <p className="text-xl text-amber-100 mb-8 max-w-2xl mx-auto">
              See how BOSSS Service Desk can reduce resolution times, improve SLA compliance,
              and make your IT team more efficient.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }}>
                <Button
                  className="bg-gradient-to-r from-amber-400 to-amber-500 text-gray-900 hover:from-amber-500 hover:to-amber-600 px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-xl font-semibold"
                  onClick={() => onNavigateToTop('/demo')}
                >
                  Schedule a Demo
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </motion.div>

              <motion.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }}>
                <Button
                  variant="outline"
                  className="px-8 py-4 text-lg rounded-lg transition-all duration-200 border-white/80 text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm"
                  onClick={() => onNavigateToTop('/contact')}
                >
                  Talk to Sales
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { icon: Building2, value: 'Unlimited', label: 'Tickets' },
              { icon: Users, value: 'AI-Powered', label: 'Automation' },
              { icon: Clock, value: 'SLA', label: 'Management' },
              { icon: Heart, value: 'ITSM', label: 'Integration' },
            ].map((stat, i) => (
              <motion.div
                key={i}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <stat.icon style={{ color: BOSSS_BLUE }} className="w-8 h-8 mx-auto mb-2" />
                <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-sm text-gray-500">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default ServiceDeskPage;
